import { MenuConfigModel } from "api/repositories/sidebarMenu/model/SidebarMenuConfigModel";
import PrivateRoute from "components/layoutComponent/PrivateRoute";
import { AuthenticationContext } from "context/AuthenticationContext";
import { focusHandling } from "cruip-js-toolkit";
import { DependenciesInjection } from "di/DependencyInjection";
import LoginFeature from "features/login/views/LoginFeature";
import AppPages from "navigations/AppPages";

import { higherOrderComponent } from "components/higherOrderComponent/HigherOrderComponent";
import GeneralFeature from "features/generals/views/GeneralFeature";
import NotFound from "features/notFound/NotFound";
import {
  createElement,
  ReactNode,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router";
import { InjectionToken } from "utils/injectionToken/InjectionToken";
import UserManagementFeature from "features/userManagement/views/UserManagementFeature";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [diInjected, setDiInjected] = useState<boolean>(false);
  const { menuConfig } = AuthenticationContext();

  const initDependency = async () => {
    try {
      await DependenciesInjection.initInjections();
      setDiInjected(true);
    } catch (error) {
      console.error("Dependency Injection error:", error);
    }
  };

  const loadingScreen = () => {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loader">
          <div className="loader--dot"></div>
          <div className="loader--dot"></div>
          <div className="loader--dot"></div>
          <div className="loader--dot"></div>
          <div className="loader--dot"></div>
          <div className="loader--dot"></div>
          <div className="loader--text"></div>
        </div>
      </div>
    );
  };

  const mapMenuToRoutes = (menu: MenuConfigModel) => {
    const Component = AppPages.getPage(menu.componentName);
    const diToken = AppPages.getToken(menu.componentName);
    const props = AppPages.getAddtionalProps(menu.componentName);

    if (!Component) return <>{loadingScreen()}</>;

    return (
      <Route
        path={menu.path}
        key={menu.path}
        element={
          <PrivateRoute path={menu.path}>
            {!diInjected ? null : (
              <Suspense fallback={loadingScreen()}>
                {diToken ? (
                  createElement(
                    higherOrderComponent(Component, {
                      token: diToken,
                      addtionalProps: props,
                    })
                  )
                ) : (
                  <Component />
                )}
              </Suspense>
            )}
          </PrivateRoute>
        }
      />
    );
  };

  const routeRendering = useMemo(() => {
    let routes: Array<ReactNode> = [];
    menuConfig?.menu?.forEach((menu) => {
      routes.push(mapMenuToRoutes(menu));
      if (menu.subMenu?.length > 0) {
        menu.subMenu.forEach((subMenu) => {
          routes.push(mapMenuToRoutes(subMenu));
        });
      }
    });
    return routes;
  }, [menuConfig]);

  const pageRendering = () => {
    if (typeof window === "undefined" || !diInjected) {
      return <>{loadingScreen()}</>;
    }
    return (
      <>
        <Route
          path={"/"}
          key={"default"}
          element={
            <PrivateRoute path={"/"} key={"default"}>
              <Suspense fallback={loadingScreen()}>
                <UserManagementFeature />
              </Suspense>
            </PrivateRoute>
          }
        />
        {/* Change password page removed; use header modal instead */}
        {routeRendering}
      </>
    );
  };

  useEffect(() => {
    if (!diInjected) {
      initDependency();
    }
  }, []);

  useEffect(() => {
    document.querySelector("html").style.scrollBehavior = "auto";
    window.scroll({ top: 0 });
    document.querySelector("html").style.scrollBehavior = "";
    focusHandling(null);
  }, [location.pathname]);

  if (!diInjected) {
    return <>{loadingScreen()}</>;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <LoginFeature
            navigate={navigate}
            token={InjectionToken.LoginViewModel}
          />
        }
      ></Route>
      <>{pageRendering()}</>
      <Route
        path="*"
        element={
          <PrivateRoute path="*">
            <NotFound />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

export default App;
